/**
 * GRCAgstPBScreen — Goods Receipt Challan Against Party Bill (4.21.5)
 * A pure goods-movement document (no tax/invoice amount) — captures physical receipt
 * of stock referencing a previously entered Party Bill, before the tax invoice arrives.
 */
import React, { useState, useMemo } from 'react';
import { Save, Grid3X3, XCircle, Search } from 'lucide-react';
import ERPAdvancedGrid from '../../../components/erp/ERPAdvancedGrid';
import ERPEditableGrid from '../../../components/erp/ERPEditableGrid';
import ERPLookupModal from '../../../components/erp/ERPLookupModal';
import DynamicFieldRenderer from '../../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../../components/erp/ERPToast';
import { FormHelp } from '../../../components/help-engine/FormHelp';
import { HelpInfo } from '../../../components/help-engine/HelpInfo';
import '../../../styles/erp-theme.css';
import { tkn, today, PARTY_BILL_COLUMNS, fetchPartyBills } from './usePartyBillMasters';

const itemColumns = () => [
  { field: 'itemCode', header: 'Item Code', width: 90, readOnly: true },
  { field: 'itemName', header: 'Item Name', width: 170, readOnly: true },
  { field: 'hsnCode', header: 'HSN Code', width: 90, readOnly: true },
  { field: 'qtyOrdered', header: 'Qty (PB)', width: 80, readOnly: true },
  { field: 'qtyReceived', header: 'Qty Received', width: 100, type: 'number' },
];

// ═══════════════════════════════════════════════════════
const GRCAgstPBGrid = ({ rows, onAddNew, onView }) => {
  const columns = [
    { field: 'grcNo', header: 'GRC No' }, { field: 'grcDate', header: 'GRC Date' }, { field: 'pbNo', header: 'Against PB' },
    { field: 'supplierName', header: 'Supplier' }, { field: 'vehicleNo', header: 'Vehicle No' }, { field: 'status', header: 'Status' },
  ];
  return <ERPAdvancedGrid title="GRC Against PB" columns={columns} data={rows} total={rows.length} idField="grcNo" nameField="supplierName" page={1} pageSize={20} onPageChange={() => {}} onPageSizeChange={() => {}} loading={false} onAddNew={onAddNew} onEdit={(row) => onView(row)} testId="grc-agst-pb-grid" showFilters={false} />;
};

// ═══════════════════════════════════════════════════════
const GRCAgstPBForm = ({ editData, mode, onGridView, onSaved }) => {
  const isView = mode === 'view';
  const token = tkn();
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();

  const [grcNo, setGrcNo] = useState(editData?.grcNo || '');
  const [grcDate, setGrcDate] = useState(editData?.grcDate || today());
  const [vehicleNo, setVehicleNo] = useState('');
  const [driverName, setDriverName] = useState('');
  const [remarks, setRemarks] = useState('');
  const [selectedPB, setSelectedPB] = useState(null);
  const [itemRows, setItemRows] = useState([]);
  const [dynValues, setDynValues] = useState({});
  const [showPBLookup, setShowPBLookup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handlePBSelect = (pb) => {
    setSelectedPB(pb);
    setItemRows((pb.items || []).map(it => ({ itemCode: it.itemCode, itemName: it.itemName, hsnCode: it.hsnCode, qtyOrdered: it.qty, qtyReceived: it.qty })));
    setShowPBLookup(false);
  };

  const totalQtyReceived = useMemo(() => itemRows.reduce((s, r) => s + (parseFloat(r.qtyReceived) || 0), 0), [itemRows]);

  const handleClear = () => { setVehicleNo(''); setDriverName(''); setRemarks(''); setSelectedPB(null); setItemRows([]); setError(''); };

  const handleSave = () => {
    if (!selectedPB) { setError('Select a Party Bill to receive goods against'); erpToast.warning('Party Bill reference is required'); return; }
    if (itemRows.length === 0) { setError('No items to receive'); return; }
    setSaving(true); setError('');
    const newGrcNo = grcNo || `GRC${Date.now().toString().slice(-8)}`;
    setGrcNo(newGrcNo);
    onSaved({ grcNo: newGrcNo, grcDate, pbNo: selectedPB.pbNo, supplierName: selectedPB.supplierName, vehicleNo, status: 'Received', items: itemRows });
    erpToast.success(`GRC ${newGrcNo} saved against ${selectedPB.pbNo} (backend persistence pending next phase)`);
    setSaving(false);
  };

  return (
    <FormHelp formName="GRCAgstPBScreen" userRole={userRole}>
    <div className="erp-module" data-testid="grc-agst-pb-form" data-dev-component="GRCAgstPBScreen" data-dev-file="screens/procurement/party-bill/GRCAgstPBScreen.js">
      <div className="erp-screen-header">
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>GRC Against PB</span></button>) : (<span className="erp-screen-title">GRC Against PB</span>)}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)', background: 'var(--erp-hover)', padding: '2px 12px', borderRadius: 4 }} data-testid="grc-no-display">{grcNo || 'Auto-Generated'}</div>
          <input type="date" value={grcDate} onChange={e => setGrcDate(e.target.value)} disabled={isView} className="erp-input" style={{ width: 140, height: 26 }} data-testid="grc-date-header" />
        </div>
        <div className="erp-screen-actions">
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving} data-testid="grc-save-btn"><Save size={13} /> {saving ? 'Saving...' : 'Save'}</button>}
          <button className="erp-btn erp-btn-ghost" onClick={onGridView} data-testid="grc-gridview-btn"><Grid3X3 size={13} /> Gridview</button>
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleClear} data-testid="grc-clear-btn"><XCircle size={13} /> Clear</button>}
        </div>
      </div>

      {error && <div className="erp-alert-error" data-testid="grc-error">{error}</div>}

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }}>
        <div className="erp-form-card" style={{ marginBottom: 8 }}>
          <div className="erp-form-section">
            <div className="erp-form-3col">
              <div>
                <div className="erp-form-row"><label className="erp-form-label required">Party Bill No<HelpInfo controlId="pbNo" controlName="Party Bill No" /></label><div className="erp-form-control" style={{ gap: 4 }}><input value={selectedPB?.pbNo || ''} readOnly placeholder="Search Party Bill..." className="erp-input" style={{ flex: 1 }} onClick={() => !isView && setShowPBLookup(true)} data-testid="grc-pbno-input" /><button className="erp-btn-icon" onClick={() => !isView && setShowPBLookup(true)} data-testid="grc-pbno-search-btn"><Search size={13} /></button></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Vehicle No<HelpInfo controlId="vehicleNo" controlName="Vehicle No" /></label><div className="erp-form-control"><input value={vehicleNo} onChange={e => setVehicleNo(e.target.value)} disabled={isView} className="erp-input" data-testid="grc-vehicleno-input" /></div></div>
              </div>
              <div>
                <div className="erp-form-row"><label className="erp-form-label">Driver Name<HelpInfo controlId="driverName" controlName="Driver Name" /></label><div className="erp-form-control"><input value={driverName} onChange={e => setDriverName(e.target.value)} disabled={isView} className="erp-input" data-testid="grc-drivername-input" /></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Remarks<HelpInfo controlId="remarks" controlName="Remarks" /></label><div className="erp-form-control"><input value={remarks} onChange={e => setRemarks(e.target.value)} disabled={isView} className="erp-input" data-testid="grc-remarks-input" /></div></div>
              </div>
              <div>
                <div style={{ border: '1px solid var(--erp-border)', borderRadius: 'var(--erp-radius)', padding: 'var(--erp-sp-2)', fontSize: 11 }} data-testid="grc-pb-info-panel">
                  <div style={{ fontWeight: 600, marginBottom: 4, textDecoration: 'underline' }}>Party Bill Info</div>
                  <div>Supplier: <strong>{selectedPB?.supplierName || '—'}</strong></div>
                  <div>PB Date: <strong>{selectedPB?.pbDate || '—'}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DynamicFieldRenderer screenName="GRCAgstPBScreen" recordId={grcNo} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />

        <div className="erp-form-card" style={{ marginTop: 8 }}>
          <div style={{ padding: 'var(--erp-sp-2)', fontWeight: 600, fontSize: 12, color: 'var(--erp-primary)' }}>Item Info {!selectedPB && <span style={{ fontWeight: 400, color: 'var(--erp-text-muted)' }}>— select a Party Bill to load items</span>}</div>
          <ERPEditableGrid columns={itemColumns()} rows={itemRows} onChange={setItemRows} idField="itemCode" testId="grc-item-grid" showAddRow={false} showDeleteRow={false} footerSummary={[{ field: 'qtyReceived', type: 'sum', header: 'Total Qty Received' }]} />
        </div>
      </div>

      {showPBLookup && <ERPLookupModal title="PARTY BILL LOOKUP" columns={PARTY_BILL_COLUMNS} fetchData={fetchPartyBills} onSelect={handlePBSelect} onClose={() => setShowPBLookup(false)} />}
    </div>
    </FormHelp>
  );
};

// ═══════════════════════════════════════════════════════
const GRCAgstPBScreen = ({ theme = 'light', onBack }) => {
  const [view, setView] = useState('grid');
  const [savedRows, setSavedRows] = useState([]);
  const [editData, setEditData] = useState(null);
  const [formMode, setFormMode] = useState('add');

  if (view === 'grid') return <GRCAgstPBGrid rows={savedRows} onAddNew={() => { setEditData(null); setFormMode('add'); setView('form'); }} onView={(row) => { setEditData(row); setFormMode('view'); setView('form'); }} />;
  return <GRCAgstPBForm editData={editData} mode={formMode} onGridView={() => setView('grid')} onSaved={(row) => { setSavedRows(prev => [row, ...prev.filter(r => r.grcNo !== row.grcNo)]); setView('grid'); }} />;
};

export default GRCAgstPBScreen;
